import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Autocomplete,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  setWarningDetailsDialog,
  editWarnings,
  setWarningUpdateBody,
} from "../../../components/collabs/collabsSlice";
import Langs, { Localize } from "../../../app/lang/langs";
import { checkPermission, getVarFormattedDate } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ── Shared style constants ── */
const EASE_SMOOTH = "cubic-bezier(0.4, 0, 0.2, 1)";
const RADIUS_CARD = "16px";
const RADIUS_INPUT = "12px";
const TRANSITION_ALL = `all 280ms ${EASE_SMOOTH}`;

/* Glassmorphic info-card surface */
const glassCardSx = {
  p: 2.5,
  borderRadius: RADIUS_CARD,
  backgroundColor: "action.hover",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)",
  transition: TRANSITION_ALL,
};

/* Shared field-label style */
const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  fontSize: "0.82rem",
};

/* Shared input sx for TextFields */
const enhancedInputSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: RADIUS_INPUT,
    transition: TRANSITION_ALL,
    "&:hover": {
      boxShadow: "0 0 0 3px rgba(255,144,21,0.08)",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(255,144,21,0.18)",
    },
  },
};

/* Section overline heading style */
const sectionOverlineSx = {
  color: "text.secondary",
  fontWeight: 700,
  mb: 1.5,
  display: "block",
  letterSpacing: "0.08em",
  fontSize: "0.68rem",
};

/* Info row styling */
const infoRowSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  py: 0.75,
  px: 0.5,
  borderRadius: "8px",
  transition: TRANSITION_ALL,
  "&:hover": {
    bgcolor: "action.selected",
  },
};

export default function MobileWarningDetailsDialog() {
  const dispatch = useDispatch();

  const visible = useSelector((state) => state.collabs.warningDialog);
  const warningData = useSelector((state) => state.collabs.warningData);

  const [tempWarningDate, setTempWarningDate] = useState(null);
  const [tempStatus, setTempStatus] = useState(null);
  const [tempComments, setTempComments] = useState("");
  const [updateBody, setUpdateBody] = useState({});

  const canEdit = checkPermission(config.scopes.warnings, config.permissionTypes.edit);

  const statusOptions = [
    { label: Localize("received"), value: "received" },
    { label: Localize("signed"), value: "signed" },
    { label: Localize("sent"), value: "sent" },
  ];

  // Initialize temp state from warningData when dialog opens
  useEffect(() => {
    if (visible && warningData) {
      if (warningData.warning_date) {
        setTempWarningDate(getVarFormattedDate(warningData.warning_date, "yyyy-mm-dd"));
      }
      if (warningData.status) {
        setTempStatus(warningData.status);
      }
      if (warningData.comments !== undefined) {
        setTempComments(warningData.comments || "");
      }
      setUpdateBody({ warning_id: warningData.id });
    }
  }, [warningData, visible]);

  // Track changes for warning_date
  useEffect(() => {
    if (!warningData) return;
    const originalDate = getVarFormattedDate(warningData.warning_date, "yyyy-mm-dd");
    if (originalDate !== tempWarningDate) {
      if (tempWarningDate) {
        setUpdateBody((current) => ({ ...current, warning_date: tempWarningDate }));
      }
    } else {
      setUpdateBody((current) => {
        const newState = { ...current };
        delete newState.warning_date;
        return newState;
      });
    }
  }, [tempWarningDate]);

  // Track changes for status
  useEffect(() => {
    if (!warningData) return;
    const originalStatus = warningData.status;
    if (originalStatus !== tempStatus) {
      if (tempStatus) {
        setUpdateBody((current) => ({ ...current, status: tempStatus }));
      }
    } else {
      setUpdateBody((current) => {
        const newState = { ...current };
        delete newState.status;
        return newState;
      });
    }
  }, [tempStatus]);

  // Track changes for comments
  useEffect(() => {
    if (!warningData) return;
    const originalComments = warningData.comments;
    if (originalComments !== tempComments) {
      setUpdateBody((current) => ({ ...current, comments: tempComments }));
    } else {
      setUpdateBody((current) => {
        const newState = { ...current };
        delete newState.comments;
        return newState;
      });
    }
  }, [tempComments]);

  const handleClose = () => {
    dispatch(setWarningDetailsDialog(false));
    setUpdateBody({});
  };

  const handleSave = () => {
    if (Object.keys(updateBody).length > 1) {
      dispatch(editWarnings(updateBody));
      dispatch(setWarningUpdateBody(updateBody));
      handleClose();
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    date.setHours(date.getHours() + 2);
    const iso = date.toISOString().substring(0, 16);
    return (
      iso.substring(8, 10) +
      "-" +
      iso.substring(5, 7) +
      "-" +
      iso.substring(0, 4) +
      " " +
      iso.substring(11, 16)
    );
  };

  const getCategoryChipProps = (category) => {
    if (category === "warning") return { color: "warning", label: Localize("warning") };
    if (category === "recall the order") return { color: "error", label: Localize("recall the order") };
    if (category === "formal notice") return { color: "info", label: Localize("formal notice") };
    return { color: "default", label: Localize(category || "") };
  };

  const formatPersonName = (value) => {
    if (value && typeof value === "object" && value.prenom && value.nom) {
      return `${value.prenom} ${value.nom}`;
    }
    return "-";
  };

  const hasChanges = Object.keys(updateBody).length > 1;
  const collabFullName =
    warningData?.collab_nom && warningData?.collab_prenom
      ? `${warningData.collab_nom} ${warningData.collab_prenom}`
      : "";

  const selectedStatus = statusOptions.find((o) => o.value === tempStatus) || null;

  return (
    <Dialog
      fullScreen
      open={!!visible}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{ zIndex: 1400 }}
    >
      {/* ── AppBar with glassmorphic surface ── */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "var(--surface-card, #fff)",
          color: "text.primary",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
          borderBottom: "1px solid",
          borderColor: "divider",
          transition: TRANSITION_ALL,
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_ALL,
              borderRadius: "12px",
              "&:hover": {
                transform: "scale(1.08)",
                bgcolor: "action.hover",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 1.5, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.2,
                fontSize: "0.72rem",
                letterSpacing: "0.04em",
                fontWeight: 500,
                textTransform: "uppercase",
                opacity: 0.7,
              }}
            >
              <Langs str="WARNING_DETAILS_FOR" />
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.3,
                letterSpacing: "-0.02em",
                fontSize: "1.1rem",
              }}
            >
              {collabFullName}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Main content area ── */}
      <Box
        sx={{
          p: 2.5,
          pb: 14,
          overflowY: "auto",
          flex: 1,
          scrollBehavior: "smooth",
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          "& > *": {
            animation: `fadeSlideIn 400ms ${EASE_SMOOTH} both`,
          },
        }}
      >
        {warningData && (
          <>
            {/* ── Read-only collab info section ── */}
            <Typography variant="overline" sx={sectionOverlineSx}>
              {Localize("COLLAB_INFORMATION")}
            </Typography>

            <Box sx={{ ...glassCardSx, mb: 2.5 }}>
              {warningData.collab_matricule && (
                <Box sx={infoRowSx}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
                    <Langs str="collab_matricule" />
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem" }}>
                    {warningData.collab_matricule}
                  </Typography>
                </Box>
              )}
              {warningData.collab_email && (
                <Box sx={infoRowSx}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
                    <Langs str="collab_email" />
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem" }}>
                    {warningData.collab_email}
                  </Typography>
                </Box>
              )}
              {warningData.poste && (
                <Box sx={infoRowSx}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
                    <Langs str="poste" />
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem" }}>
                    {warningData.poste}
                  </Typography>
                </Box>
              )}
              {warningData.team_name && (
                <Box sx={infoRowSx}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
                    <Langs str="team_name" />
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem" }}>
                    {warningData.team_name}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 2.5, opacity: 0.4 }} />

            {/* ── Category (read-only) ── */}
            <Typography variant="body2" sx={fieldLabelSx}>
              <Langs str="category" />
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Chip
                size="medium"
                {...getCategoryChipProps(warningData.category)}
                sx={{
                  fontWeight: 600,
                  borderRadius: "10px",
                  px: 0.5,
                  fontSize: "0.82rem",
                  letterSpacing: "0.01em",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  transition: TRANSITION_ALL,
                }}
              />
            </Box>

            {/* ── Status (editable if permission) ── */}
            <Typography variant="body2" sx={fieldLabelSx}>
              <Langs str="status" />
            </Typography>
            {canEdit ? (
              <Autocomplete
                options={statusOptions}
                getOptionLabel={(option) => option.label || ""}
                value={selectedStatus}
                onChange={(_, newValue) => setTempStatus(newValue ? newValue.value : null)}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={Localize("status")}
                    inputProps={{ ...params.inputProps, style: { fontSize: "16px" } }}
                  />
                )}
                slotProps={{
                  popper: { sx: { zIndex: 1500 } },
                }}
                sx={enhancedInputSx}
              />
            ) : (
              <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                {Localize(warningData.status || "")}
              </Typography>
            )}

            {/* ── Warning Date (editable if permission) ── */}
            <Typography variant="body2" sx={fieldLabelSx}>
              <Langs str="warning_date" />
            </Typography>
            {canEdit ? (
              <TextField
                type="date"
                fullWidth
                value={tempWarningDate || ""}
                onChange={(e) => setTempWarningDate(e.target.value)}
                inputProps={{ style: { fontSize: "16px" } }}
                sx={enhancedInputSx}
              />
            ) : (
              <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                {tempWarningDate || "-"}
              </Typography>
            )}

            {/* ── Comments (editable if permission) ── */}
            <Typography variant="body2" sx={fieldLabelSx}>
              <Langs str="comments" />
            </Typography>
            {canEdit ? (
              <TextField
                multiline
                rows={4}
                fullWidth
                value={tempComments}
                onChange={(e) => setTempComments(e.target.value)}
                inputProps={{ style: { fontSize: "16px" }, maxLength: 200 }}
                sx={enhancedInputSx}
              />
            ) : (
              <Typography variant="body1" sx={{ mb: 3, whiteSpace: "pre-wrap", fontWeight: 400 }}>
                {warningData.comments || "-"}
              </Typography>
            )}

            <Divider sx={{ mb: 2.5, opacity: 0.4 }} />

            {/* ── Metadata section (read-only) ── */}
            <Typography variant="overline" sx={sectionOverlineSx}>
              <Langs str="HISTORY" />
            </Typography>

            <Box sx={glassCardSx}>
              <Box sx={infoRowSx}>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
                  <Langs str="created_at" />
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem", fontVariantNumeric: "tabular-nums" }}>
                  {formatTimestamp(warningData.created_at)}
                </Typography>
              </Box>
              <Box sx={infoRowSx}>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
                  <Langs str="updated_at" />
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem", fontVariantNumeric: "tabular-nums" }}>
                  {formatTimestamp(warningData.updated_at)}
                </Typography>
              </Box>
              {warningData.created_by && (
                <Box sx={infoRowSx}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
                    <Langs str="created_by" />
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem" }}>
                    {formatPersonName(warningData.created_by)}
                  </Typography>
                </Box>
              )}
              {warningData.updated_by && (
                <Box sx={infoRowSx}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
                    <Langs str="updated_by" />
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem" }}>
                    {formatPersonName(warningData.updated_by)}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* ── Sticky bottom - Save button with glassmorphic backdrop ── */}
      {canEdit && hasChanges && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            p: 2.5,
            bgcolor: "var(--surface-card, #fff)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.06), 0 -1px 4px rgba(0,0,0,0.03)",
            borderTop: "1px solid",
            borderColor: "divider",
            zIndex: 1,
            animation: `fadeSlideIn 300ms ${EASE_SMOOTH} both`,
            "@keyframes fadeSlideIn": {
              from: { opacity: 0, transform: "translateY(12px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Button
            variant="contained"
            color="warning"
            fullWidth
            onClick={handleSave}
            sx={{
              minHeight: 52,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "1rem",
              letterSpacing: "-0.01em",
              boxShadow: "0 4px 16px rgba(255,144,21,0.3)",
              transition: TRANSITION_ALL,
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: "0 6px 24px rgba(255,144,21,0.4)",
              },
              "&:active": {
                transform: "scale(0.98)",
                boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
              },
            }}
          >
            <Langs str="SAVE" />
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
